import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Alert, Button, Stack, TextField, ButtonBase,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Switch
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../context/ToastContext';
import { fetchLibrarySettings, updateLibrarySettings } from '../slices/librarySlice';
import LibraryDashboardTab from '../components/library/LibraryDashboardTab';
import LibraryCatalogTab from '../components/library/LibraryCatalogTab';
import LibraryBorrowingTab from '../components/library/LibraryBorrowingTab';
import LibraryReportsTab from '../components/library/LibraryReportsTab';
import {
  C, FONT, HEADER_GRADIENT, inputSx, primaryButtonSx, dialogPaperProps, dialogTitleSx
} from '../components/library/libraryUi';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SettingsIcon from '@mui/icons-material/Settings';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboardOutlined';
import AutoStoriesIcon from '@mui/icons-material/AutoStoriesOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

const TABS = [
  { label: 'نظرة عامة', icon: <SpaceDashboardIcon fontSize="small" /> },
  { label: 'الكتب', icon: <AutoStoriesIcon fontSize="small" /> },
  { label: 'الاستعارة والإرجاع', icon: <SwapHorizIcon fontSize="small" /> },
  { label: 'التقارير', icon: <InsightsIcon fontSize="small" /> },
];

// Loan days, max books per borrower and the daily late fine
function LibrarySettingsDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { settings, saving } = useSelector((state) => state.library);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (open) dispatch(fetchLibrarySettings());
  }, [open, dispatch]);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const handleSave = async () => {
    try {
      await dispatch(updateLibrarySettings({
        loan_days: Number(form.loan_days),
        max_items_per_student: Number(form.max_items_per_student),
        fine_per_day: form.fine_per_day,
        auto_calculate_fines: form.auto_calculate_fines,
      })).unwrap();
      showToast('تم حفظ إعدادات المكتبة', 'success');
      onClose();
    } catch (error) {
      showToast(error?.error || 'حدث خطأ', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={dialogPaperProps}>
      <DialogTitle sx={dialogTitleSx}>إعدادات المكتبة</DialogTitle>
      <DialogContent>
        {form && (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="مدة الاستعارة (أيام)"
              type="number"
              value={form.loan_days}
              onChange={(e) => setForm({ ...form, loan_days: e.target.value })}
              inputProps={{ min: 1 }}
              sx={inputSx}
            />
            <TextField
              label="الحد الأقصى للكتب لكل مستعير"
              type="number"
              value={form.max_items_per_student}
              onChange={(e) => setForm({ ...form, max_items_per_student: e.target.value })}
              inputProps={{ min: 1 }}
              sx={inputSx}
            />
            <Box sx={{ p: 2, borderRadius: 3, bgcolor: C.bg, border: `1px solid ${C.border}` }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.auto_calculate_fines}
                    onChange={(e) => setForm({ ...form, auto_calculate_fines: e.target.checked })}
                  />
                }
                label={<Typography sx={{ fontFamily: FONT, fontWeight: 700 }}>غرامة التأخير</Typography>}
              />
              {form.auto_calculate_fines && (
                <TextField
                  label="المبلغ لكل يوم تأخير"
                  type="number"
                  value={form.fine_per_day}
                  onChange={(e) => setForm({ ...form, fine_per_day: e.target.value })}
                  inputProps={{ min: 0, step: 0.5 }}
                  fullWidth
                  size="small"
                  sx={{ ...inputSx, mt: 1.5 }}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ fontFamily: FONT, color: C.muted }}>إلغاء</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || !form} sx={primaryButtonSx}>
          {saving ? 'جاري الحفظ...' : 'حفظ'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const LibraryPage = () => {
  const { error } = useSelector((state) => state.library);
  const [activeTab, setActiveTab] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const pageSx = {
    direction: 'rtl',
    fontFamily: FONT,
    p: { xs: 2, md: 3 },
    bgcolor: C.bg,
    minHeight: '100vh',
  };

  if (error && error.error === 'المكتبة غير مفعلة') {
    return (
      <Box sx={pageSx}>
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Alert severity="warning" sx={{ borderRadius: 3, fontFamily: FONT }}>
            المكتبة غير مفعلة لهذا الحساب. يرجى التواصل مع المدير لتفعيلها.
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={pageSx}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 2.5,
            borderRadius: 4,
            background: HEADER_GRADIENT,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative book spines */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute', left: 24, bottom: 0, display: { xs: 'none', md: 'flex' },
              alignItems: 'flex-end', gap: 0.75, opacity: 0.18,
            }}
          >
            {[58, 74, 50, 82, 64, 70, 46].map((h, i) => (
              <Box key={i} sx={{ width: 14, height: h, bgcolor: '#fff', borderRadius: '3px 3px 0 0' }} />
            ))}
          </Box>

          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5} sx={{ position: 'relative' }}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 3,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MenuBookIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} color="#fff" sx={{ fontFamily: FONT }}>
                المكتبة
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.25, fontFamily: FONT }}>
                الكتب، الاستعارة والإرجاع في مكان واحد
              </Typography>
            </Box>
          </Stack>
          <Button
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsOpen(true)}
            sx={{
              position: 'relative',
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 2.5,
              px: 2,
              fontFamily: FONT,
              fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
            }}
          >
            الإعدادات
          </Button>
        </MotionPaper>

        <LibrarySettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />

        {/* Tabs */}
        <Box
          role="tablist"
          sx={{
            display: 'flex', gap: 0.5, p: 0.5, mb: 2.5,
            bgcolor: '#fff', border: `1px solid ${C.border}`, borderRadius: 3.5,
            overflowX: 'auto', width: { xs: '100%', md: 'fit-content' },
          }}
        >
          {TABS.map((tab, index) => {
            const active = index === activeTab;
            return (
              <ButtonBase
                key={tab.label}
                role="tab"
                aria-selected={active}
                onClick={() => setActiveTab(index)}
                sx={{
                  flexShrink: 0,
                  gap: 1, px: 2, py: 1, borderRadius: 3,
                  fontFamily: FONT, fontWeight: 700, fontSize: '0.9rem',
                  color: active ? '#fff' : C.muted,
                  background: active ? `linear-gradient(135deg, ${C.primary}, ${C.violet})` : 'transparent',
                  transition: 'all .15s',
                  '&:hover': { color: active ? '#fff' : C.primary },
                }}
              >
                {tab.icon}
                {tab.label}
              </ButtonBase>
            );
          })}
        </Box>

        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {activeTab === 0 && <LibraryDashboardTab onOpenTab={setActiveTab} />}
          {activeTab === 1 && <LibraryCatalogTab />}
          {activeTab === 2 && <LibraryBorrowingTab />}
          {activeTab === 3 && <LibraryReportsTab />}
        </motion.div>
      </Box>
    </Box>
  );
};

export default LibraryPage;
